import { useProjectState } from '@hooks/useProject'
import React, { FC } from 'react'
import { Overlay } from 'ui'
import { useTranslations } from 'use-intl'
import { RichTooltip } from './RichTooltip'
import { StudioRichTooltipProps } from './StudioRichTooltipPresets'

export const StudioRichTooltip: FC<StudioRichTooltipProps> = ({
  overlayWrapperConfig,
  offsetPxByUISize,
  ...props
}) => {
  const t = useTranslations()
  const sizePreference = useProjectState('sizePreference')

  /* 
    Overlay use case #1:
    The builtin Tooltip `delayed` prop only works on self-managed tooltips.
    To have a non-self-managed tooltip be delayed, wrap it in an Overlay
    and set the Overlay's `delayed` property to true.
  */

  /*
    Overlay use case #2:
    The builtin Tooltip `offset` only controls the offset in one direction.
    ex1: placement="bottom": The `offset` prop will only control the y-axis offset.
    ex2: placement="right": The `offset` prop will only control the x-axis offset.
    An Overlay element can accept both x and y offsets as an array.
    This is useful for fine-tuning the position of the Tooltip when needed.
  */

  /*
    Safari does not properly support the `auto` type for Overlays.
    We need to use the `hint` type instead.
    This means that the Overlay/Tooltip will not be dismissed on interaction with the anchor element.
  */
  const isSafari = /^((?!chrome).)*safari/i.test(navigator.userAgent)

  return (
    <Overlay
      type={isSafari ? 'hint' : 'auto'}
      data-testid="studio-rich-tooltip-overlay"
      receivesFocus="false"
      placement={props.placement}
      delayed={props.delayed ?? true}
      {...overlayWrapperConfig}
    >
      <RichTooltip
        {...props}
        selfManaged={false}
        description={t(props.description)}
        keybind={props.keybind ? t(props.keybind) : undefined}
        offsetPx={offsetPxByUISize?.[sizePreference] || props.offsetPx}
        title={t(props.title)}
      />
    </Overlay>
  )
}
